import { FC, Fragment, useEffect, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { useField, useFormikContext } from 'formik';
import { useQuery } from 'react-query';
import axios from 'axios';
import Http from '../../../services/http';
import { useGlobalState } from '../../../contexts/GlobalStateProvider';

interface PlantCode {
  id: number;
  uuid: string;
  ClientCode: string;
  PlantCode: string;
  PlantName: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  name: string;
  label: string;
}

export const PlantCodeSelect: FC<Props> = ({ name, label }) => {
  const [field, meta, helpers] = useField(name);
  const [clientCodeField, clientCodeMeta, clientCodeHelpers] =
    useField('client_code');
  const [selected, setSelected] = useState<PlantCode | null>(null);
  const [query, setQuery] = useState('');
  const { errors, touched } = useFormikContext<{ [key: string]: string }>();

  const { currentClient } = useGlobalState();

  const { data: plantCodes } = useQuery<PlantCode[]>('plantCodes', async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API}/plant-codes`,
      {
        headers: {
          Authorization: `Bearer ${Http.accessToken}`,
        },
      }
    );
    return data;
  });

  const displayedPlantCodes = plantCodes?.filter(
    (code) => code.ClientCode === currentClient
  );

  const displayPlantCodes = displayedPlantCodes?.length
    ? displayedPlantCodes
    : [
        {
          id: 0,
          uuid: '0',
          ClientCode: 'N/A',
          PlantCode: 'N/A',
          PlantName: 'N/A',
          created_at: '',
          updated_at: '',
        },
      ];

  const formatPlantName = (fullName: string) => {
    const parts = fullName?.split('-')?.slice(0, -1);
    return parts?.join(' - ').trim();
  };

  useEffect(() => {
    helpers.setValue(selected?.PlantCode || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    setSelected(null);
    helpers.setValue('');
    clientCodeHelpers.setValue(currentClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClient]);

  const filteredPlantCodes =
    query === ''
      ? displayPlantCodes
      : displayPlantCodes.filter((plantCode) => {
          const searchStr = `${plantCode.PlantCode} ${formatPlantName(
            plantCode.PlantName
          )}`.toLowerCase();
          return searchStr.includes(query.toLowerCase());
        });

  return (
    <div className="col-span-1">
      <Combobox value={selected} onChange={setSelected}>
        {({ open }) => (
          <>
            <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900">
              {label}
            </Combobox.Label>
            <div className="relative mt-2">
              <div className="relative w-full">
                <Combobox.Input
                  className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={(plantCode: PlantCode | null) =>
                    plantCode ? plantCode.PlantCode : ''
                  }
                  placeholder="Search plant code..."
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </div>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {filteredPlantCodes.length === 0 && query !== '' ? (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                      Nothing found.
                    </div>
                  ) : (
                    filteredPlantCodes.map((plantCode) => (
                      <Combobox.Option
                        key={plantCode.uuid}
                        value={plantCode}
                        className={({ active }) =>
                          classNames(
                            active
                              ? 'bg-indigo-600 text-white'
                              : 'text-gray-900',
                            'relative cursor-default select-none py-2 pl-3 pr-9'
                          )
                        }
                      >
                        {({ selected, active }) => (
                          <>
                            <div className="flex flex-col">
                              <span
                                className={classNames(
                                  selected ? 'font-semibold' : 'font-normal',
                                  'block text-sm'
                                )}
                              >
                                {plantCode.PlantCode}
                              </span>
                              <span
                                className={classNames(
                                  active ? 'text-indigo-50' : 'text-gray-500',
                                  'block text-xs whitespace-normal'
                                )}
                              >
                                {formatPlantName(plantCode.PlantName)}
                              </span>
                            </div>

                            {selected ? (
                              <span
                                className={classNames(
                                  active ? 'text-white' : 'text-indigo-600',
                                  'absolute inset-y-0 right-0 flex items-center pr-4'
                                )}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </>
        )}
      </Combobox>
      {errors[name] && touched[name] ? (
        <p className="mt-2 text-sm text-red-600" id="email-error">
          {errors[name]}
        </p>
      ) : null}
    </div>
  );
};
